import type {
  IAmenityRepository,
  AmenityQueryParams,
  PaginatedResponse,
} from '@yawi/domain';
import { buildQueryString, DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '@yawi/domain';
import type {
  Amenity,
  AmenityAvailability,
  WeeklyScheduleDay,
  BlackoutDate,
  CancellationPolicy,
  ReminderConfig,
  ReservationFrequencyLimit,
  AmenitySectionPrice,
} from '@yawi/types';
import { AmenityPricingMode, ChargeTiming } from '@yawi/types';
import { getApiClient } from '../../http/apiClient';

interface ApiAmenityResponse {
  id: string;
  name: string;
  code?: string | null;
  capacity: number;
  imageUrl?: string | null;
  imageFileId?: string | null;
  requiresDeposit: boolean;
  depositAmount?: number | string | null;
  costPerHour?: number | string | null;
  pricingMode?: AmenityPricingMode | null;
  sectionPrices?: AmenitySectionPrice[];
  minHours?: number | string | null;
  maxHours?: number | string | null;
  rules?: string | null;
  termsAndConditions?: string | null;
  isActive: boolean;
  chargeTiming?: string | null;
  requiresApproval?: boolean;
  autoConfirmIfPaid?: boolean;
  accessControlEnabled?: boolean;
  timezone?: string | null;
  weeklySchedule?: WeeklyScheduleDay[] | null;
  blackoutDates?: BlackoutDate[] | null;
  cancellationPolicy?: CancellationPolicy | null;
  reminderConfig?: ReminderConfig | null;
  accessControlDoorIds?: string[] | null;
  reservationFrequencyLimit?: ReservationFrequencyLimit | null;
  sectionIds?: string[];
  createdAt: string;
  updatedAt: string;
}

const toNonNegativeNumber = (value: unknown): number => {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
};

const toPositiveNumber = (value: unknown, fallback: number): number => {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

const mapApiAmenityToAmenity = (api: ApiAmenityResponse): Amenity => {
  const requiresDeposit = api.requiresDeposit ?? false;

  return {
    id: api.id,
    name: api.name,
    code: api.code ?? null,
    capacity: toPositiveNumber(api.capacity, 1),
    imageUrl: api.imageUrl ?? null,
    imageFileId: api.imageFileId ?? null,
    requiresDeposit,
    depositAmount: requiresDeposit ? toNonNegativeNumber(api.depositAmount) : 0,
    costPerHour: toNonNegativeNumber(api.costPerHour),
    pricingMode: api.pricingMode ?? AmenityPricingMode.PER_HOUR,
    sectionPrices: api.sectionPrices ?? [],
    minHours: toPositiveNumber(api.minHours, 1),
    maxHours: toPositiveNumber(api.maxHours, toPositiveNumber(api.minHours, 1)),
    rules: api.rules ?? null,
    termsAndConditions: api.termsAndConditions ?? null,
    isActive: api.isActive,
    chargeTiming: (api.chargeTiming as ChargeTiming) ?? null,
    requiresApproval: api.requiresApproval ?? false,
    autoConfirmIfPaid: api.autoConfirmIfPaid ?? false,
    accessControlEnabled: api.accessControlEnabled ?? false,
    timezone: api.timezone ?? null,
    weeklySchedule: api.weeklySchedule ?? null,
    blackoutDates: api.blackoutDates ?? null,
    cancellationPolicy: api.cancellationPolicy ?? null,
    reminderConfig: api.reminderConfig ?? null,
    accessControlDoorIds: api.accessControlDoorIds ?? null,
    reservationFrequencyLimit: api.reservationFrequencyLimit ?? null,
    sectionIds: api.sectionIds ?? [],
    createdAt: api.createdAt,
    updatedAt: api.updatedAt,
  };
};

const normalizeAmenityPayload = <T extends Partial<Omit<Amenity, 'id' | 'createdAt' | 'updatedAt'>>>(amenity: T): T => ({
  ...amenity,
  depositAmount: amenity.requiresDeposit ? toNonNegativeNumber(amenity.depositAmount) : 0,
  costPerHour: toNonNegativeNumber(amenity.costPerHour),
  capacity: toNonNegativeNumber(amenity.capacity),
  minHours: toNonNegativeNumber(amenity.minHours),
  maxHours: toNonNegativeNumber(amenity.maxHours),
});

export class ApiAmenityRepository implements IAmenityRepository {
  async getAmenities(params?: AmenityQueryParams): Promise<PaginatedResponse<Amenity>> {
    const client = getApiClient();
    const queryString = buildQueryString({
      page: params?.page ?? DEFAULT_PAGE,
      limit: params?.limit ?? DEFAULT_PAGE_SIZE,
      search: params?.search,
      sortBy: params?.sortBy,
      sortOrder: params?.sortOrder,
      ...(params?.activeOnly ? { activeOnly: 'true' } : {}),
    });
    const response = await client.get<PaginatedResponse<ApiAmenityResponse>>(
      `v1/amenities?${queryString}`
    );
    return {
      ...response,
      data: response.data.map(mapApiAmenityToAmenity),
    };
  }

  async getAmenityById(id: string): Promise<Amenity | null> {
    const client = getApiClient();
    try {
      const api = await client.get<ApiAmenityResponse>(`v1/amenities/${id}`);
      return mapApiAmenityToAmenity(api);
    } catch {
      return null;
    }
  }

  async createAmenity(
    amenity: Omit<Amenity, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<Amenity> {
    const client = getApiClient();
    const api = await client.post<ApiAmenityResponse>('v1/amenities', normalizeAmenityPayload(amenity));
    return mapApiAmenityToAmenity(api);
  }

  async updateAmenity(
    id: string,
    data: Partial<Omit<Amenity, 'id' | 'createdAt' | 'updatedAt'>>
  ): Promise<Amenity> {
    const client = getApiClient();
    const api = await client.put<ApiAmenityResponse>(`v1/amenities/${id}`, normalizeAmenityPayload(data));
    return mapApiAmenityToAmenity(api);
  }

  async deleteAmenity(id: string): Promise<void> {
    const client = getApiClient();
    await client.delete(`v1/amenities/${id}`);
  }

  async getAvailability(amenityId: string, date: string): Promise<AmenityAvailability> {
    const client = getApiClient();
    return await client.get<AmenityAvailability>(
      `v1/amenities/${amenityId}/availability?date=${encodeURIComponent(date)}`
    );
  }
}
